<template>
  <div class="w-full xl:w-full flex flex-col space-y-11">
    <div class="relative w-full">
      <FormSelect
        v-model="vacancy"
        :options="vacanciesOptions"
        :invalid="$v.vacancy.$error"
        :placeholder="`${$t('landing.choose-vacancy')}*`"
        @search-blur="checkData('vacancy')"
      >
        <template #label>
          <div>{{ $t('landing.choose-vacancy') }}*</div>
        </template>
      </FormSelect>
      <div v-if="$v.vacancy.$error" class="absolute text-danger-50 text-xs left-5 -bottom-5">
        {{ $t('landing.choose-vacancy') }}
      </div>
    </div>

    <div class="relative w-full">
      <FormSelect
        :value="city"
        :options="regionsFormattedOptions"
        :invalid="$v.city.$error"
        searchable
        :placeholder="`${$t('landing.city')}*`"
        @input="regionsInputHandler"
        @search-blur="checkData('city')"
      >
        <template #label>
          <div>{{ $t('landing.city') }}*</div>
        </template>
      </FormSelect>
      <div v-if="$v.city.$error" class="absolute text-danger-50 text-xs left-5 -bottom-5">
        {{ $t('landing.error:city') }}
      </div>
    </div>
    <div class="relative w-full">
      <FormSelect
        v-model="shop"
        :options="officesFilteredOptions"
        :invalid="$v.shop.$error"
        :disabled="!city"
        searchable
        :placeholder="`${$t('landing.store')}*`"
        @search-blur="checkData('shop')"
      >
        <template #label>
          <div>{{ $t('landing.store') }}*</div>
        </template>
      </FormSelect>
      <div v-if="$v.shop.$error" class="absolute text-danger-50 text-xs left-5 -bottom-5">
        {{ $t('landing.error:store') }}
      </div>
    </div>
    <div class="flex lg:flex-col lg:space-y-11 sm:space-y-9 space-x-4 lg:space-x-0">
      <div class="relative w-1/2 lg:w-full">
        <FormSelect
          v-model="schedule"
          :options="schedulesOptions"
          :invalid="$v.schedule.$error"
          :placeholder="`${$t('landing.prefered-schedule')}*`"
          @search-blur="checkData('schedule')"
        >
          <template #label>
            <div>{{ $t('landing.prefered-schedule') }}*</div>
          </template>
        </FormSelect>
        <div v-if="$v.schedule.$error" class="absolute text-danger-50 text-xs left-5 -bottom-5">
          {{ $t('landing.error:prefered-schedule') }}
        </div>
      </div>
      <div class="relative w-1/2 lg:w-full">
        <FormInput
          v-model="profit"
          :invalid="$v.profit.$error"
          :placeholder="`${$t('landing.expected-income-level')}*`"
          @blur="checkData('profit')"
        >
          <template #label>
            <div>{{ $t('landing.expected-income-level') }}*</div>
          </template>
        </FormInput>
        <div v-if="$v.profit.$error" class="absolute text-danger-50 text-xs left-5 -bottom-5">
          {{ $t('landing.error:expected-income-level') }}
        </div>
      </div>

    </div>
    <div class="flex sm:flex-col sm:space-y-4 space-x-4 sm:space-x-0">
      <Button
        palette="neutral-200"
        @click="goStepBack"
        class="w-1/2 sm:w-full"
      >
        {{ $t('landing.previous-step') }}
      </Button>
      <Button @click="submitVacancyInfo" class="w-1/2 sm:w-full">{{ $t('landing.next') }}</Button>
    </div>
  </div>
</template>
<script>
import { required, minLength, maxLength } from 'vuelidate/lib/validators';
import FormInput from '@/components/Form/FormInput.vue';
import FormSelect from '@/components/Form/FormSelect.vue';
import Button from '@/components/UI/Button.vue';
import toast from '@/notify';
import { getVacancy } from '@/api';
import sanitize from '@/sanitize';

export default {
  name: 'VacancyInfo',
  components: {
    FormInput,
    FormSelect,
    Button,
  },
  props: {
    regionsOptions: {
      type: Array,
      default: () => [],
    },
    officesOptions: {
      type: Array,
      default: () => [],
    },
    schedulesOptions: {
      type: Array,
      default: () => [],
    },
    vacanciesOptions: {
      type: Array,
      default: () => [],
    },
  },
  data() {
    return {
      vacancy: '',
      city: '',
      cityId: '',
      shop: '',
      schedule: '',
      profit: '',
      departments: [],
    };
  },
  async mounted() {
    const { position, office } = this.$route.query;
    if (position) {
      const { status, data } = await getVacancy({ position, office });
      if (status === 200) {
        this.vacancy = data.vacancy.name;
        this.shop = data.office.name;
        if (office) {
          const currentOffice = this.officesOptions?.find(({ id }) => id === office);
          const currentRegion = this.regionsFormattedOptions.find(({ ids }) => ids.includes(currentOffice.cityId));
          this.city = currentRegion.key;
        }
      }
    }
    const storageData = JSON.parse(sessionStorage.getItem('vacancyInfo'));
    if (storageData) {
      this.vacancy = storageData.vacancy;
      this.city = storageData.city;
      this.cityId = storageData.cityId;
      this.shop = storageData.shop;
      this.schedule = storageData.schedule;
      this.profit = storageData.profit;
    }
  },
  validations: {
    vacancy: {
      required,
    },
    city: {
      required,
    },
    schedule: {
      required,
    },
    shop: {
      required,
    },
    profit: {
      required,
      minLength: minLength(2),
      maxLength: maxLength(80),
      symbolsTest: (value) => !/[\\|/'"`]/g.test(value),
    },
  },
  computed: {
    vacancyOptions() {
      return [
        this.$t('landing.loader'),
        this.$t('landing.sales-representative'),
        this.$t('landing.system-administrator'),
      ];
    },
    regionsFormattedOptions() {
      const cityNames = this.regionsOptions.map(({ name }) => name);
      const uniqCityNames = Array.from(new Set(cityNames));

      return uniqCityNames.map((city) => {
        const ids = this.regionsOptions
          .filter(({ name }) => name === city)
          .map(({ id }) => id);
        return { key: city, label: city, ids };
      });
    },
    officesFilteredOptions() {
      return this.officesOptions
        .filter(({ city }) => (this.city ? city === this.city : true))
        .map(({ name }) => name);
    },
  },
  methods: {
    submitVacancyInfo() {
      this.$v.$touch();
      if (!this.$v.$invalid) {
        const formData = {
          vacancy: this.vacancy,
          city: this.city,
          cityId: this.shop
            ? sanitize(this.officesOptions.find(({ name }) => name === this.shop).cityId)
            : this.departments[0],
          shop: this.shop,
          schedule: this.schedule,
          profit: sanitize(this.profit),
        };
        this.$emit('vacancy-info-ready', formData);
      } else {
        toast.danger({
          text: this.$t('landing.fill-required-fields'),
        });
      }
    },
    goStepBack() {
      this.$emit('go-prev');
    },
    checkData(d) {
      this.$v[d].$touch();
    },
    regionsInputHandler(e) {
      this.city = e.label;
      this.departments = e.ids;
      this.shop = '';
    },
  },
};
</script>
